import isEqual from "lodash/isEqual";
import type {
  CollectionDetailedSerializer,
  OkResponse_CollectionDetailedSerializer_,
  IApiResponse,
  ArtShortSerializer,
  CollectionWithRubricsSerializer,
  CollectionPaginationResponse,
  CollectionReactionEnum,
} from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";

export const journalApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    /* Не понятно почему все данные для журнала запрашиваются из эндпоинта коллекций,
     * хотя это разные сущности, но пока так.
     * TODO возможно после задачи https://jira.mybook.tech/browse/TECHN-2297 что-то изменится
     * возможно нужно будет вынести в отдельную API */
    getJournalArticleBySlug: build.query<CollectionDetailedSerializer, string>({
      query: slug => ({
        url: `/foundation/api/collections/journal/${slug}`,
        /* для этого эндпоинта настроено кэширование через хэдер "cache-control"
         * https://mybook.slack.com/archives/C02ARHCBKL5/p1710949094814369
         * чтобы работала инвалидаця кэша нам надо отменить это поведение */
        headers: {
          "cache-control": "no-cache",
        },
      }),
      transformResponse: (
        response: OkResponse_CollectionDetailedSerializer_
      ) => {
        return response.payload.data;
      },
      providesTags: (_result, _error, slug) => [
        { type: TagsType.COLLECTION, id: slug },
      ],
    }),
    getJournalArticleArts: build.query<
      IApiResponse<ArtShortSerializer[]>["payload"],
      { slug: string; limit: number; offset: number }
    >({
      query: ({ slug, offset, limit }) => ({
        url: `/foundation/api/collections/journal/${slug}/arts`,
        params: {
          limit,
          offset,
        },
        headers: {
          "accept-version": "2",
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}_${queryArgs.slug}`;
      },
      merge: (currentCacheData, responseData) => {
        return {
          ...responseData,
          data: [...currentCacheData.data, ...responseData.data],
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      transformResponse: (response: IApiResponse<ArtShortSerializer[]>) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),
    getJournalSimilarArticlesBySlug: build.query<
      CollectionWithRubricsSerializer[],
      {
        slug: string;
        limit: number;
      }
    >({
      query: ({ slug, limit }) => ({
        url: `/foundation/api/collections/journal/${slug}/similar`,
        params: {
          limit,
        },
      }),
      transformResponse: (response: CollectionPaginationResponse) => {
        return response.payload.data;
      },
    }),
    addLikeToJournalArticleBySlug: build.mutation<void, string>({
      query: slug => {
        return {
          url: `/foundation/api/collections/journal/${slug}/likes`,
          method: "PUT",
        };
      },
      async onQueryStarted(slug, { dispatch, queryFulfilled }) {
        const optimisticPatchedArticle = dispatch(
          journalApi.util.updateQueryData(
            "getJournalArticleBySlug",
            slug,
            draftArticle => {
              const isArticleDislikedByUser =
                draftArticle.user_reaction === "disliked";

              Object.assign<
                CollectionDetailedSerializer,
                Partial<CollectionDetailedSerializer>
              >(draftArticle, {
                // TODO Выпилить кастинг после перехода на union
                user_reaction: "liked" as CollectionReactionEnum,
                likes: draftArticle.likes + 1,
                ...(isArticleDislikedByUser && {
                  dislikes: draftArticle.dislikes - 1,
                }),
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedArticle.undo();
        }
      },
      invalidatesTags: (_result, _error, slug) => [
        { type: TagsType.COLLECTION, id: slug },
      ],
    }),
    removeLikeFromJournalArticleBySlug: build.mutation<void, string>({
      query: slug => {
        return {
          url: `/foundation/api/collections/journal/${slug}/likes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(slug, { dispatch, queryFulfilled }) {
        const optimisticPatchedArticle = dispatch(
          journalApi.util.updateQueryData(
            "getJournalArticleBySlug",
            slug,
            draftArticle => {
              Object.assign<
                CollectionDetailedSerializer,
                Partial<CollectionDetailedSerializer>
              >(draftArticle, {
                // TODO Выпилить кастинг после перехода на union
                user_reaction: "none" as CollectionReactionEnum,
                likes: draftArticle.likes - 1,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedArticle.undo();
        }
      },
      invalidatesTags: (_result, _error, slug) => [
        { type: TagsType.COLLECTION, id: slug },
      ],
    }),
    addDislikeToJournalArticleBySlug: build.mutation<string, string>({
      query: slug => {
        return {
          url: `/foundation/api/collections/journal/${slug}/dislikes`,
          method: "PUT",
        };
      },
      async onQueryStarted(slug, { dispatch, queryFulfilled }) {
        const optimisticPatchedArticle = dispatch(
          journalApi.util.updateQueryData(
            "getJournalArticleBySlug",
            slug,
            draftArticle => {
              const isArticleLikedByUser =
                draftArticle.user_reaction === "liked";

              Object.assign<
                CollectionDetailedSerializer,
                Partial<CollectionDetailedSerializer>
              >(draftArticle, {
                // TODO Выпилить кастинг после перехода на union
                user_reaction: "disliked" as CollectionReactionEnum,
                dislikes: draftArticle.dislikes + 1,
                ...(isArticleLikedByUser && { likes: draftArticle.likes - 1 }),
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedArticle.undo();
        }
      },
      invalidatesTags: (_result, _error, slug) => [
        { type: TagsType.COLLECTION, id: slug },
      ],
    }),
    removeDislikeFromJournalArticleBySlug: build.mutation<string, string>({
      query: slug => {
        return {
          url: `/foundation/api/collections/journal/${slug}/dislikes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(slug, { dispatch, queryFulfilled }) {
        const optimisticPatchedArticle = dispatch(
          journalApi.util.updateQueryData(
            "getJournalArticleBySlug",
            slug,
            draftArticle => {
              Object.assign<
                CollectionDetailedSerializer,
                Partial<CollectionDetailedSerializer>
              >(draftArticle, {
                // TODO Выпилить кастинг после перехода на union
                user_reaction: "none" as CollectionReactionEnum,
                dislikes: draftArticle.dislikes - 1,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedArticle.undo();
        }
      },
      invalidatesTags: (_result, _error, slug) => [
        { type: TagsType.COLLECTION, id: slug },
      ],
    }),
  }),
});
